import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Input, Button, Space } from 'antd';
import { EditFilled } from '@ant-design/icons';
import { useQuery } from 'hooks/apollo';

import Page from 'components/Page';
import Table from 'components/Table';

import globalMessages from 'translations/messages';
import useQueryParams from 'hooks/useQueryParams';
import messages from './messages';

import personalDataReviewQueueQuery from './personalDataReviewQueue.gql';
import { latestFlaggedAt, timestampFromCuid } from './reviewQueue';
import RedactPersonalInfo from './RedactPersonalInfo';
import RedactPiiScanPersonalInfo from './RedactPiiScanPersonalInfo';

const FlaggedComments = () => {
  const params = useQueryParams();
  const [search, setSearch] = useState();
  const [editPiiScanId, setEditPiiScanId] = useState();
  const [editFlaggedCommentId, setEditFlaggedCommentId] = useState();
  const surveyGroupId = params.get('surveyGroupId') || undefined;
  const { data, loading, refetch } = useQuery(personalDataReviewQueueQuery, { variables: { surveyGroupId }, fetchPolicy: 'cache-first' });

  const dataSource = useMemo(() => {
    const rowsByQuestionResponseId = new Map();

    const ensureRow = (questionResponse, respondedAt, customer, survey, question) => {
      const questionResponseId = questionResponse.id;
      if (!rowsByQuestionResponseId.has(questionResponseId)) {
        rowsByQuestionResponseId.set(questionResponseId, {
          rowKey: `qr:${questionResponseId}`,
          questionResponseId,
          respondedAt,
          customer,
          survey,
          question,
          legacyHighlights: [],
          piiHighlights: [],
          piiFlaggedAt: null,
          legacyFlaggedAt: null,
        });
      }
      return rowsByQuestionResponseId.get(questionResponseId);
    };

    const stampFlaggedAt = (row) => {
      row.flaggedAt = latestFlaggedAt(row.piiFlaggedAt, row.legacyFlaggedAt);
    };

    (data?.piiScansPendingReview || []).forEach((scan) => {
      const { questionResponse } = scan;
      const row = ensureRow(
        questionResponse,
        questionResponse.surveyResponse.respondedAt,
        questionResponse.surveyResponse.survey.customer.name,
        questionResponse.surveyResponse.survey.title,
        questionResponse.question.title,
      );
      row.piiScanId = scan.id;
      row.piiFlaggedAt = scan.createdAt;
      stampFlaggedAt(row);
      row.piiHighlights = Array.isArray(scan.result?.entities)
        ? scan.result.entities.map((e) => (e.entity != null ? String(e.entity) : '')).filter(Boolean)
        : [];
    });

    (data?.flaggedComments || []).forEach((fc) => {
      const { questionResponse } = fc;
      const row = ensureRow(
        questionResponse,
        questionResponse.surveyResponse.respondedAt,
        questionResponse.surveyResponse.survey.customer.name,
        questionResponse.surveyResponse.survey.title,
        questionResponse.question.title,
      );
      row.flaggedCommentId = fc.id;
      row.legacyFlaggedAt = timestampFromCuid(fc.id);
      stampFlaggedAt(row);
      row.legacyHighlights = fc.meta.map((m) => m.value);
    });

    const merged = [...rowsByQuestionResponseId.values()];
    merged.sort((a, b) => new Date(b.flaggedAt || 0) - new Date(a.flaggedAt || 0));
    return merged;
  }, [data?.piiScansPendingReview, data?.flaggedComments]);

  const dataSourceRef = useRef([]);
  useEffect(() => {
    dataSourceRef.current = dataSource;
  }, [dataSource]);

  const getNextRowKey = (currentRowKey) => {
    const rows = dataSourceRef.current;
    const idx = rows.findIndex((r) => r.rowKey === currentRowKey);
    if (idx === -1 || idx >= rows.length - 1) {
      return undefined;
    }
    return rows[idx + 1].rowKey;
  };

  const openLegacyEditor = (row) => {
    setEditPiiScanId(undefined);
    setEditFlaggedCommentId(row.flaggedCommentId);
  };

  const openPiiEditor = (row) => {
    setEditFlaggedCommentId(undefined);
    setEditPiiScanId(row.piiScanId);
  };

  const handleModalDone = (advance, followUpRowKey, editorKind) => {
    if (!advance) {
      setEditPiiScanId(undefined);
      setEditFlaggedCommentId(undefined);
      refetch();
      return;
    }

    const row = dataSourceRef.current.find((r) => r.rowKey === followUpRowKey);
    if (!row) {
      setEditPiiScanId(undefined);
      setEditFlaggedCommentId(undefined);
      refetch();
      return;
    }

    if (editorKind === 'legacy' && row.flaggedCommentId) {
      openLegacyEditor(row);
      return;
    }
    if (editorKind === 'pii' && row.piiScanId) {
      openPiiEditor(row);
      return;
    }
    if (row.flaggedCommentId) {
      openLegacyEditor(row);
      return;
    }
    if (row.piiScanId) {
      openPiiEditor(row);
      return;
    }

    setEditPiiScanId(undefined);
    setEditFlaggedCommentId(undefined);
    refetch();
  };

  const columns = [
    {
      dataIndex: 'flaggedAt',
      title: <FormattedMessage {...messages.flaggedAt} />,
      type: 'relativeDate',
      defaultSortOrder: 'descend',
    },
    {
      dataIndex: 'respondedAt',
      title: <FormattedMessage {...globalMessages.date} />,
      type: 'relativeDate',
    },
    {
      dataIndex: 'customer',
      title: <FormattedMessage {...globalMessages.customer} />,
    },
    {
      dataIndex: 'survey',
      title: <FormattedMessage {...globalMessages.survey} />,
    },
    {
      dataIndex: 'question',
      title: <FormattedMessage {...globalMessages.question} />,
      render: (question) => <span dangerouslySetInnerHTML={{ __html: question }} />,
    },
    {
      dataIndex: 'highlights',
      title: 'Personal data',
      render: (_, row) => [...row.legacyHighlights, ...row.piiHighlights].join(', '),
      search: (_, searchText, row) => [...row.legacyHighlights, ...row.piiHighlights]
        .find((h) => h.toLowerCase().search(searchText) !== -1),
    },
  ];

  const actions = (row) => (
    <Space wrap>
      {row.flaggedCommentId && (
        <Button
          icon={<EditFilled />}
          onClick={() => openLegacyEditor(row)}
        >
          <FormattedMessage {...messages.resolveLegacy} />
        </Button>
      )}
      {row.piiScanId && (
        <Button
          icon={<EditFilled />}
          onClick={() => openPiiEditor(row)}
        >
          <FormattedMessage {...messages.resolvePii} />
        </Button>
      )}
    </Space>
  );

  return (
    <Page>
      <Page.Header title={<FormattedMessage {...messages.flaggedComments} />} />
      <Page.Content>
        <Input.Search onSearch={setSearch} allowClear placeholder="Search" style={{ marginBottom: '1em' }} />
        <Table
          rowKey="rowKey"
          loading={loading}
          columns={columns}
          dataSource={dataSource}
          search={search}
          actions={actions}
        />
        {editPiiScanId && (
          <RedactPiiScanPersonalInfo
            id={editPiiScanId}
            rowKey={dataSourceRef.current.find((row) => row.piiScanId === editPiiScanId)?.rowKey}
            onDone={handleModalDone}
            getNextRowKey={getNextRowKey}
          />
        )}
        {editFlaggedCommentId && (
          <RedactPersonalInfo
            id={editFlaggedCommentId}
            rowKey={dataSourceRef.current.find((row) => row.flaggedCommentId === editFlaggedCommentId)?.rowKey}
            onDone={handleModalDone}
            getNextRowKey={getNextRowKey}
          />
        )}
      </Page.Content>
    </Page>
  );
};

export default FlaggedComments;
